export function MarketInsights() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Small Apartment Building Market Insights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding the market for small multifamily properties helps you make informed decisions about selling your duplex, triplex, quadplex, or five-unit building.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Why Small Apartment Buildings Are Valuable
            </h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Strong Investor Demand</h4>
                  <p className="text-gray-600">
                    Small multifamily properties are highly sought after by investors because they offer multiple income streams with manageable property management requirements. This consistent demand helps maintain property values even in challenging markets.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-home-heart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Residential Financing Available</h4>
                  <p className="text-gray-600">
                    Properties with 2-4 units qualify for residential financing with lower down payments and better interest rates than commercial properties. This makes them accessible to a broader range of buyers, supporting property values.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Income Diversification</h4>
                  <p className="text-gray-600">
                    Multiple units mean multiple tenants, reducing the risk of total income loss. If one unit becomes vacant, the other units continue generating rental income, making these properties more stable investments.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-scales-3-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Economies of Scale</h4>
                  <p className="text-gray-600">
                    Small apartment buildings benefit from shared systems and structures, making per-unit maintenance and operating costs lower than single-family rentals while remaining manageable for individual owners.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/0850327d2280caf474f17ef1c3d56f96.jpg"
              alt="Market Insights"
              className="w-full h-full object-cover rounded-xl shadow-2xl"
            />
          </div>
        </div>

        <div className="bg-amber-50 rounded-2xl p-8 lg:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Current Market Trends for Small Multifamily Properties
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-arrow-up-line text-green-500 text-2xl mr-3"></i>
                Rising Rental Demand
              </h4>
              <p className="text-gray-600">
                Housing affordability challenges continue to drive strong rental demand across the United States. More people are choosing to rent rather than buy, creating consistent tenant demand for well-located small apartment buildings.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-funds-line text-orange-500 text-2xl mr-3"></i>
                Investor Competition
              </h4>
              <p className="text-gray-600">
                Institutional and individual investors actively compete for small multifamily properties, recognizing their value as stable, income-producing assets. This competition supports strong property valuations.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-home-gear-line text-orange-500 text-2xl mr-3"></i>
                Value-Add Opportunities
              </h4>
              <p className="text-gray-600">
                Many older small apartment buildings offer significant value-add potential through renovations and improved management. Investors actively seek these opportunities, making even distressed properties valuable.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-map-pin-2-line text-orange-500 text-2xl mr-3"></i>
                Location Flexibility
              </h4>
              <p className="text-gray-600">
                Small multifamily properties exist in virtually every neighborhood and market, from urban centers to suburban areas. This geographic diversity provides opportunities for sellers in all locations.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl lg:text-4xl font-bold mb-6 text-center">
              What This Means for You as a Seller
            </h3>
            <p className="text-xl mb-8 text-center opacity-90">
              Strong market fundamentals mean your small apartment building has value, regardless of its current condition or challenges. We understand these market dynamics and make offers that reflect the true potential of your property.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">Fair</div>
                <p className="opacity-90">Market-based valuations</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">Fast</div>
                <p className="opacity-90">7-14 day closings</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">Certain</div>
                <p className="opacity-90">Guaranteed cash offers</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}